// ============================================================================
// KLINISOL CRO - Quiz Review Component (REDESIGNED)
// Pure Tailwind CSS - Matches Dashboard/Navbar design system
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import trainingApi from '../../services/trainingApi';
import { 
    X, 
    CheckCircle, 
    XCircle, 
    Printer, 
    Lightbulb,
    Loader2,
    AlertCircle 
} from 'lucide-react';

const QuizReview = () => {
    const { attemptId } = useParams();
    const navigate = useNavigate();
    
    const [reviewData, setReviewData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filterType, setFilterType] = useState('all');

    useEffect(() => {
        loadReviewData();
    }, [attemptId]);

    const loadReviewData = async () => {
        try {
            setLoading(true);
            setError('');
            
            const data = await trainingApi.getAttemptResults(attemptId);
            setReviewData(data);
            
        } catch (err) {
            console.error('Error loading review data:', err);
            setError('Failed to load quiz review. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    
    const getFilteredQuestions = () => {
        if (!reviewData) return [];
        
        if (filterType === 'correct') {
            return reviewData.review.filter(item => item.is_correct);
        } else if (filterType === 'incorrect') {
            return reviewData.review.filter(item => !item.is_correct);
        }
        return reviewData.review;
    };
    
    const getCorrectCount = () => {
        return reviewData?.review.filter(item => item.is_correct).length || 0;
    };
    
    const getIncorrectCount = () => {
        return reviewData?.review.filter(item => !item.is_correct).length || 0;
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-[#f8fafd] to-[#e0e7ff] gap-6">
                <Loader2 className="w-16 h-16 text-[#475569] animate-spin" />
                <p className="text-lg text-gray-600 font-medium">
                    Loading quiz review...
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-[#f8fafd] to-[#e0e7ff] p-8">
                <div className="max-w-lg bg-white rounded-2xl shadow-xl p-12 text-center">
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center border-4 border-red-500">
                        <XCircle className="w-12 h-12 text-red-500" />
                    </div>
                    <p className="mb-6 text-lg text-red-900 font-semibold">
                        {error}
                    </p>
                    <button 
                        onClick={() => window.close()}
                        className="px-8 py-3.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold hover:-translate-y-0.5 transition-all shadow-lg"
                    >
                        Close Window
                    </button>
                </div>
            </div>
        );
    }

    if (!reviewData) {
        return null;
    }

    const filteredQuestions = getFilteredQuestions();

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#f8fafd] to-[#e0e7ff] p-8">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-2xl shadow-md p-8 mb-8">
                    <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
                        <h1 className="text-4xl font-bold text-gray-900">
                            Quiz Review
                        </h1>
                        <button 
                            onClick={() => window.close()}
                            className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-600 border-2 border-gray-200 rounded-xl font-semibold hover:bg-gray-200 hover:text-gray-900 transition-all"
                        >
                            <X className="w-5 h-5" />
                            <span>Close</span>
                        </button>
                    </div>
                    
                    {/* Score Summary */}
                    <div className={`rounded-2xl p-8 border-4 shadow-lg mb-8 ${
                        reviewData.passed 
                            ? 'bg-gradient-to-br from-green-100 to-green-50 border-green-500' 
                            : 'bg-gradient-to-br from-red-100 to-red-50 border-red-500'
                    }`}>
                        <div className="flex items-center gap-6 flex-wrap">
                            <div className={`w-20 h-20 rounded-full flex items-center justify-center text-white text-4xl shadow-lg ${
                                reviewData.passed 
                                    ? 'bg-gradient-to-br from-green-500 to-green-600' 
                                    : 'bg-gradient-to-br from-red-500 to-red-600'
                            }`}>
                                {reviewData.passed ? (
                                    <CheckCircle className="w-12 h-12" />
                                ) : (
                                    <XCircle className="w-12 h-12" />
                                )}
                            </div>
                            
                            <div className="flex-1">
                                <div className={`text-3xl font-bold mb-3 ${
                                    reviewData.passed ? 'text-green-800' : 'text-red-800'
                                }`}>
                                    {reviewData.passed ? 'PASSED' : 'FAILED'}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div>
                                        <div className={`text-xs font-semibold mb-1 ${
                                            reviewData.passed ? 'text-green-700' : 'text-red-700'
                                        }`}>
                                            SCORE
                                        </div>
                                        <div className={`text-2xl font-bold ${
                                            reviewData.passed ? 'text-green-800' : 'text-red-800'
                                        }`}>
                                            {reviewData.score}/{reviewData.total_points} ({reviewData.percentage}%)
                                        </div>
                                    </div>
                                    <div>
                                        <div className={`text-xs font-semibold mb-1 ${
                                            reviewData.passed ? 'text-green-700' : 'text-red-700'
                                        }`}>
                                            ATTEMPT
                                        </div>
                                        <div className={`text-2xl font-bold ${
                                            reviewData.passed ? 'text-green-800' : 'text-red-800'
                                        }`}>
                                            #{reviewData.attempt_number}
                                        </div>
                                    </div>
                                    <div>
                                        <div className={`text-xs font-semibold mb-1 ${
                                            reviewData.passed ? 'text-green-700' : 'text-red-700'
                                        }`}>
                                            SUBMITTED
                                        </div>
                                        <div className={`text-base font-semibold ${
                                            reviewData.passed ? 'text-green-800' : 'text-red-800'
                                        }`}>
                                            {new Date(reviewData.submitted_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Filter Controls */}
                    <div className="flex justify-between items-center flex-wrap gap-4">
                        <div className="flex gap-3 flex-wrap">
                            <button
                                onClick={() => setFilterType('all')}
                                className={`px-5 py-3 rounded-xl font-semibold transition-all ${
                                    filterType === 'all'
                                        ? 'bg-gradient-to-r from-[#475569] to-[#334155] text-white shadow-lg'
                                        : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-[#475569]'
                                }`}
                            >
                                All Questions ({reviewData.review.length})
                            </button>
                            <button
                                onClick={() => setFilterType('correct')}
                                className={`px-5 py-3 rounded-xl font-semibold transition-all ${
                                    filterType === 'correct'
                                        ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg'
                                        : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-green-500'
                                }`}
                            >
                                <CheckCircle className="w-4 h-4 inline mr-1" /> Correct ({getCorrectCount()})
                            </button>
                            <button
                                onClick={() => setFilterType('incorrect')}
                                className={`px-5 py-3 rounded-xl font-semibold transition-all ${
                                    filterType === 'incorrect'
                                        ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg'
                                        : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-red-500'
                                }`}
                            >
                                <XCircle className="w-4 h-4 inline mr-1" /> Incorrect ({getIncorrectCount()})
                            </button>
                        </div>
                        
                        <button 
                            onClick={() => window.print()}
                            className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-[#E91E63] to-[#C2185B] text-white rounded-xl font-semibold hover:-translate-y-0.5 transition-all shadow-lg"
                        >
                            <Printer className="w-4 h-4" />
                            <span>Print Review</span>
                        </button>
                    </div>
                </div>
                
                {/* Questions Review */}
                <div className="flex flex-col gap-6">
                    {filteredQuestions.length === 0 ? (
                        <div className="bg-white rounded-2xl shadow-md p-16 text-center">
                            <p className="text-lg text-gray-600">
                                No questions match the selected filter
                            </p>
                        </div>
                    ) : (
                        filteredQuestions.map((item, index) => (
                            <div 
                                key={index}
                                className={`bg-white rounded-2xl shadow-md p-6 border-4 ${
                                    item.is_correct ? 'border-green-500' : 'border-red-500'
                                }`}
                            >
                                {/* Question Header */}
                                <div className="flex justify-between items-center mb-4 pb-4 border-b-2 border-gray-200 flex-wrap gap-3">
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-bold text-gray-600 uppercase">
                                            Question {item.question_id}
                                        </span>
                                        <span className={`px-4 py-1.5 rounded-full text-sm font-bold border-2 ${
                                            item.is_correct
                                                ? 'bg-gradient-to-r from-green-100 to-green-50 text-green-800 border-green-500'
                                                : 'bg-gradient-to-r from-red-100 to-red-50 text-red-800 border-red-500'
                                        }`}>
                                            {item.is_correct ? (
                                                <><CheckCircle className="w-3 h-3 inline mr-1" /> Correct</>
                                            ) : (
                                                <><XCircle className="w-3 h-3 inline mr-1" /> Incorrect</>
                                            )}
                                        </span>
                                    </div>
                                    <div className="text-sm font-semibold text-gray-600 bg-gray-100 px-4 py-1.5 rounded-full">
                                        {item.points_earned} / {item.points_possible} points
                                    </div>
                                </div>
                                
                                {/* Question Text */}
                                <div className="mb-6">
                                    <p className="text-lg text-gray-900 leading-relaxed font-medium">
                                        {item.question_text}
                                    </p>
                                </div>
                                
                                {/* Answer Review */}
                                <div className={`grid gap-4 mb-6 ${item.is_correct ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
                                    <div className={`p-4 rounded-xl border-2 ${
                                        item.is_correct
                                            ? 'bg-gradient-to-br from-green-100 to-green-50 border-green-500'
                                            : 'bg-gradient-to-br from-red-100 to-red-50 border-red-500'
                                    }`}>
                                        <div className={`text-xs font-bold uppercase mb-2 ${
                                            item.is_correct ? 'text-green-800' : 'text-red-800'
                                        }`}>
                                            Your Answer:
                                        </div>
                                        <div className="flex items-center gap-3 font-bold">
                                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-white ${
                                                item.is_correct ? 'bg-green-500' : 'bg-red-500'
                                            }`}>
                                                {item.is_correct ? (
                                                    <CheckCircle className="w-4 h-4" />
                                                ) : (
                                                    <XCircle className="w-4 h-4" />
                                                )}
                                            </span>
                                            <span className={item.is_correct ? 'text-green-800' : 'text-red-800'}>
                                                {item.selected_answer}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {!item.is_correct && (
                                        <div className="bg-gradient-to-br from-green-100 to-green-50 p-4 rounded-xl border-2 border-green-500">
                                            <div className="text-xs font-bold text-green-800 uppercase mb-2">
                                                Correct Answer:
                                            </div>
                                            <div className="flex items-center gap-3 text-green-800 font-bold">
                                                <span className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center text-white">
                                                    <CheckCircle className="w-4 h-4" />
                                                </span>
                                                <span>{item.correct_answer}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                {/* All Options */}
                                {item.options && item.options.length > 0 && (
                                    <div className="mb-6">
                                        <h4 className="text-sm font-bold text-gray-900 uppercase mb-3">
                                            All Options:
                                        </h4>
                                        <div className="flex flex-col gap-2">
                                            {item.options.map((option) => {
                                                const isSelected = option.key === item.selected_answer;
                                                const isCorrect = option.key === item.correct_answer;
                                                
                                                return (
                                                    <div 
                                                        key={option.key}
                                                        className={`p-3.5 rounded-xl border-2 flex items-center gap-3 ${
                                                            isCorrect
                                                                ? 'bg-gradient-to-r from-green-100 to-green-50 border-green-500'
                                                                : isSelected
                                                                ? 'bg-gradient-to-r from-red-100 to-red-50 border-red-500'
                                                                : 'bg-gray-50 border-gray-200'
                                                        }`}
                                                    >
                                                        <span className={`font-bold min-w-[24px] ${
                                                            isCorrect ? 'text-green-800' : isSelected ? 'text-red-800' : 'text-gray-600'
                                                        }`}>
                                                            {option.key}.
                                                        </span>
                                                        <span className={`flex-1 ${
                                                            isCorrect ? 'text-green-800' : isSelected ? 'text-red-800' : 'text-gray-900'
                                                        }`}>
                                                            {option.text}
                                                        </span>
                                                        {isSelected && (
                                                            <span className={`text-xs font-bold px-2.5 py-1 rounded-lg ${
                                                                isCorrect ? 'bg-white/50 text-green-800' : 'bg-white/50 text-red-800'
                                                            }`}>
                                                                ← Your Answer
                                                            </span>
                                                        )}
                                                        {isCorrect && !isSelected && (
                                                            <span className="text-xs font-bold bg-white/50 text-green-800 px-2.5 py-1 rounded-lg">
                                                                ← Correct
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Explanation */}
                                {item.explanation && (
                                    <div className="bg-gradient-to-br from-gray-100 to-gray-50 border-2 border-[#475569] p-5 rounded-xl">
                                        <div className="flex gap-3 items-start">
                                            <Lightbulb className="w-6 h-6 text-[#475569] flex-shrink-0" />
                                            <div>
                                                <h4 className="text-sm font-bold text-[#475569] uppercase mb-2">
                                                    Explanation:
                                                </h4>
                                                <p className="text-[#475569] leading-relaxed">
                                                    {item.explanation}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
                
                {/* Footer */}
                <div className="bg-white rounded-2xl shadow-md p-8 mt-8 text-center">
                    {!reviewData.passed && (
                        <div className="bg-gradient-to-br from-gray-100 to-gray-50 border-2 border-[#475569] p-6 rounded-xl mb-6">
                            <p className="text-lg text-[#475569] leading-relaxed">
                                <strong>Need to improve?</strong> Review the explanations above and 
                                study the training materials. You can retake this quiz when ready.
                            </p>
                        </div>
                    )}
                    
                    {reviewData.passed && (
                        <div className="bg-gradient-to-br from-green-100 to-green-50 border-2 border-green-500 p-6 rounded-xl mb-6">
                            <p className="text-lg text-green-800 leading-relaxed">
                                <strong>Great job!</strong> You've successfully passed this quiz. 
                                Review the explanations to reinforce your learning.
                            </p>
                        </div>
                    )}
                    
                    <button 
                        onClick={() => window.close()}
                        className="px-10 py-4 bg-gradient-to-r from-[#475569] to-[#334155] text-white rounded-xl font-bold hover:-translate-y-0.5 transition-all shadow-lg text-lg"
                    >
                        Close Review
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QuizReview;